import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const MemberHome = ({ member, setActivePage }) => {
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);
  const [borrowedCostumes, setBorrowedCostumes] = useState([]);
  const [borrowedInstruments, setBorrowedInstruments] = useState([]);
  const [fines, setFines] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showReturnCostume, setShowReturnCostume] = useState(false);
  const [showReturnInstrument, setShowReturnInstrument] = useState(false);
  const [selectedReturns, setSelectedReturns] = useState([]);
  const [returnMessage, setReturnMessage] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [att, cos, inst, fin, perf, ann] = await Promise.all([
        API.get(`/attendance/member/${member.id}`),
        API.get(`/costumes/member/${member.id}`),
        API.get(`/instruments/member/${member.id}`),
        API.get(`/fines/member/${member.id}`),
        API.get(`/performances/member/${member.id}`),
        API.get('/announcements'),
      ]);
      setWeeklyAttendance(att.data);
      setBorrowedCostumes(cos.data);
      setBorrowedInstruments(inst.data);
      setFines(fin.data);
      setPerformances(perf.data);
      setAnnouncements(ann.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  // Calendar helpers
  const getDaysInMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const getPerformancesForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return performances.filter(p => p.date?.startsWith(dateStr));
  };

  const isEventDone = (date) => new Date(date) < new Date();

  const today = new Date();
  const isToday = (day) =>
    day === today.getDate() &&
    currentDate.getMonth() === today.getMonth() &&
    currentDate.getFullYear() === today.getFullYear();

  // Weekly attendance
  const getWeekDates = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });
  };

  const getAttendanceForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return weeklyAttendance.find(a => a.date?.startsWith(dateStr));
  };

  const handleReturnCostume = async () => {
    if (selectedReturns.length === 0) {
      setReturnMessage('Please select at least one costume!');
      return;
    }
    try {
      for (const id of selectedReturns) {
        await API.put(`/costumes/borrowings/${id}/request-return`);
      }
      setReturnMessage('✅ Return request submitted!');
      setTimeout(() => {
        setShowReturnCostume(false);
        setSelectedReturns([]);
        setReturnMessage('');
        fetchAll();
      }, 2000);
    } catch (error) {
      setReturnMessage('Error submitting return!');
    }
  };

  const handleReturnInstrument = async () => {
    if (selectedReturns.length === 0) {
      setReturnMessage('Please select at least one instrument!');
      return;
    }
    try {
      for (const id of selectedReturns) {
        await API.put(`/instruments/borrowings/${id}/request-return`);
      }
      setReturnMessage('✅ Return request submitted!');
      setTimeout(() => {
        setShowReturnInstrument(false);
        setSelectedReturns([]);
        setReturnMessage('');
        fetchAll();
      }, 2000);
    } catch (error) {
      setReturnMessage('Error submitting return!');
    }
  };

  const toggleReturn = (id) => {
    if (selectedReturns.includes(id)) {
      setSelectedReturns(selectedReturns.filter(r => r !== id));
    } else {
      setSelectedReturns([...selectedReturns, id]);
    }
  };

  const unpaidFines = fines.filter(f => f.status === 'unpaid');
  const totalFines = unpaidFines.reduce(
    (sum, f) => sum + parseFloat(f.amount), 0
  );
  const weekDates = getWeekDates();
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDay(currentDate);

  if (loading) return (
    <div style={styles.loading}>
      <p>Loading your dashboard...</p>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Welcome Banner */}
      <div style={styles.banner}>
        <div>
          <h2 style={styles.bannerTitle}>
            Welcome, {member?.first_name}! 👋
          </h2>
          <p style={styles.bannerSub}>
            {member?.role?.replace('_', ' ')} · Dulimbay Cultural Group
          </p>
        </div>
        <p style={styles.bannerDate}>
          {today.toLocaleDateString('en-PH', {
            weekday: 'long', year: 'numeric',
            month: 'long', day: 'numeric'
          })}
        </p>
      </div>

      {/* Announcements */}
      {announcements.length > 0 && (
        <div style={styles.annBox}>
          <p style={styles.annTitle}>📢 Announcements</p>
          {announcements.map(ann => (
            <div key={ann.id} style={styles.annItem}>
              <p style={styles.annItemTitle}>{ann.title}</p>
              <p style={styles.annItemContent}>{ann.content}</p>
            </div>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div style={styles.cardsRow}>
        <div style={{ ...styles.card, borderTop: '4px solid #f39c12' }}
          onClick={() => setShowReturnCostume(true)}>
          <p style={styles.cardIcon}>👘</p>
          <p style={styles.cardValue}>{borrowedCostumes.length}</p>
          <p style={styles.cardLabel}>Borrowed Costumes</p>
          {borrowedCostumes.length > 0 && (
            <p style={styles.cardAction}>Tap to return →</p>
          )}
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #9b59b6' }}
          onClick={() => setShowReturnInstrument(true)}>
          <p style={styles.cardIcon}>🎵</p>
          <p style={styles.cardValue}>{borrowedInstruments.length}</p>
          <p style={styles.cardLabel}>Borrowed Instruments</p>
          {borrowedInstruments.length > 0 && (
            <p style={styles.cardAction}>Tap to return →</p>
          )}
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #e74c3c' }}>
          <p style={styles.cardIcon}>💰</p>
          <p style={styles.cardValue}>₱{totalFines.toFixed(2)}</p>
          <p style={styles.cardLabel}>Unpaid Fines</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #2ecc71' }}>
          <p style={styles.cardIcon}>🎭</p>
          <p style={styles.cardValue}>{performances.length}</p>
          <p style={styles.cardLabel}>My Performances</p>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.mainRow}>
        {/* Left Column */}
        <div style={styles.leftCol}>
          {/* Weekly Attendance */}
          <div style={styles.box}>
            <h3 style={styles.boxTitle}>📋 This Week's Attendance</h3>
            <div style={styles.weekRow}>
              {weekDates.map((date, i) => {
                const att = getAttendanceForDay(date);
                const isCurrentDay = date.toDateString() === today.toDateString();
                return (
                  <div key={i} style={{
                    ...styles.dayBox,
                    background: att?.status === 'present' ? '#e0ffe0' :
                      att?.status === 'absent' ? '#ffe0e0' :
                        att?.status === 'late' ? '#fff3e0' :
                          isCurrentDay ? '#e8f0fe' : '#f8f9fa',
                    border: isCurrentDay ? '2px solid #0f3460' : '1px solid #eee',
                  }}>
                    <p style={styles.dayName}>{days[i]}</p>
                    <p style={styles.dayDate}>{date.getDate()}</p>
                    <p style={{
                      ...styles.attStatus,
                      color: att?.status === 'present' ? '#006600' :
                        att?.status === 'absent' ? '#cc0000' :
                          att?.status === 'late' ? '#cc7700' : '#aaa',
                    }}>
                      {att?.status === 'present' ? '✓' :
                        att?.status === 'absent' ? '✗' :
                          att?.status === 'late' ? '⏰' : '—'}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Borrowed Costumes */}
          <div style={styles.box}>
            <div style={styles.boxHeader}>
              <h3 style={styles.boxTitle}>👘 Borrowed Costumes</h3>
              {borrowedCostumes.length > 0 && (
                <button style={styles.returnBtn}
                  onClick={() => setShowReturnCostume(true)}>
                  Return
                </button>
              )}
            </div>
            {borrowedCostumes.length === 0 ? (
              <p style={styles.emptyText}>No borrowed costumes!</p>
            ) : (
              borrowedCostumes.map(c => {
                const isOverdue = new Date(c.deadline) < new Date();
                return (
                  <div key={c.id} style={styles.borrowItem}>
                    <div style={styles.borrowIcon}>👘</div>
                    <div style={styles.borrowInfo}>
                      <p style={styles.borrowName}>
                        {c.category_name} {c.type_name}
                      </p>
                      <p style={styles.borrowCode}>{c.costume_name}</p>
                      <p style={{
                        ...styles.borrowDeadline,
                        color: isOverdue ? '#e74c3c' : '#888',
                      }}>
                        Return by: {new Date(c.deadline)
                          .toLocaleDateString('en-PH')}
                        {isOverdue && ' ⚠️ Overdue!'}
                      </p>
                    </div>
                    <span style={{
                      ...styles.borrowStatus,
                      background: c.status === 'return_requested' ?
                        '#e8f0fe' : '#fff3e0',
                      color: c.status === 'return_requested' ?
                        '#0f3460' : '#cc7700',
                    }}>
                      {c.status === 'return_requested' ?
                        'Return Requested' : 'Borrowed'}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Borrowed Instruments */}
          <div style={styles.box}>
            <div style={styles.boxHeader}>
              <h3 style={styles.boxTitle}>🎵 Borrowed Instruments</h3>
              {borrowedInstruments.length > 0 && (
                <button style={styles.returnBtn}
                  onClick={() => setShowReturnInstrument(true)}>
                  Return
                </button>
              )}
            </div>
            {borrowedInstruments.length === 0 ? (
              <p style={styles.emptyText}>No borrowed instruments!</p>
            ) : (
              borrowedInstruments.map(i => {
                const isOverdue = new Date(i.deadline) < new Date();
                return (
                  <div key={i.id} style={styles.borrowItem}>
                    <div style={styles.borrowIcon}>🎵</div>
                    <div style={styles.borrowInfo}>
                      <p style={styles.borrowName}>{i.category_name}</p>
                      <p style={styles.borrowCode}>{i.instrument_name}</p>
                      <p style={{
                        ...styles.borrowDeadline,
                        color: isOverdue ? '#e74c3c' : '#888',
                      }}>
                        Return by: {new Date(i.deadline)
                          .toLocaleDateString('en-PH')}
                        {isOverdue && ' ⚠️ Overdue!'}
                      </p>
                    </div>
                    <span style={{
                      ...styles.borrowStatus,
                      background: i.status === 'return_requested' ?
                        '#e8f0fe' : '#fff3e0',
                      color: i.status === 'return_requested' ?
                        '#0f3460' : '#cc7700',
                    }}>
                      {i.status === 'return_requested' ?
                        'Return Requested' : 'Borrowed'}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Fines */}
          {fines.length > 0 && (
            <div style={styles.box}>
              <h3 style={styles.boxTitle}>💰 My Fines</h3>
              {fines.map(f => (
                <div key={f.id} style={styles.fineItem}>
                  <div style={styles.fineInfo}>
                    <p style={styles.fineReason}>{f.reason}</p>
                    <p style={styles.fineDate}>
                      {new Date(f.created_at).toLocaleDateString('en-PH')}
                    </p>
                  </div>
                  <div style={styles.fineRight}>
                    <p style={{
                      ...styles.fineAmount,
                      color: f.status === 'unpaid' ? '#e74c3c' : '#2ecc71',
                    }}>
                      ₱{parseFloat(f.amount).toFixed(2)}
                    </p>
                    <span style={{
                      ...styles.finePill,
                      background: f.status === 'unpaid' ?
                        '#ffe0e0' : '#e0ffe0',
                      color: f.status === 'unpaid' ?
                        '#cc0000' : '#006600',
                    }}>
                      {f.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Column - Calendar */}
        <div style={styles.rightCol}>
          <div style={styles.box}>
            <div style={styles.calHeader}>
              <button style={styles.calNavBtn}
                onClick={() => setCurrentDate(new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth() - 1
                ))}>◀</button>
              <h3 style={styles.calTitle}>
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              <button style={styles.calNavBtn}
                onClick={() => setCurrentDate(new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth() + 1
                ))}>▶</button>
            </div>

            {/* Day Labels */}
            <div style={styles.dayLabels}>
              {days.map(d => (
                <div key={d} style={styles.dayLabel}>{d}</div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div style={styles.calGrid}>
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`e-${i}`} style={styles.emptyCell} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dayPerfs = getPerformancesForDate(day);
                const hasPerf = dayPerfs.length > 0;
                return (
                  <div
                    key={day}
                    style={{
                      ...styles.calDay,
                      ...(isToday(day) ? styles.todayDay : {}),
                      cursor: hasPerf ? 'pointer' : 'default',
                    }}
                    onClick={() => hasPerf && setSelectedEvent({
                      day, performances: dayPerfs
                    })}
                  >
                    <span style={isToday(day) ?
                      styles.todayNum : styles.dayNum}>
                      {day}
                    </span>
                    {hasPerf && (
                      <div style={styles.dotsRow}>
                        {dayPerfs.map((p, idx) => (
                          <div key={idx} style={{
                            ...styles.dot,
                            background: isEventDone(p.date) ?
                              '#2ecc71' : '#e74c3c',
                          }} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={styles.legend}>
              <div style={styles.legendItem}>
                <div style={{
                  ...styles.legendDot,
                  background: '#e74c3c'
                }} />
                <span style={styles.legendText}>Upcoming</span>
              </div>
              <div style={styles.legendItem}>
                <div style={{
                  ...styles.legendDot,
                  background: '#2ecc71'
                }} />
                <span style={styles.legendText}>Done</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Return Costume Modal */}
      {showReturnCostume && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>👘 Return Costume</h3>
            <p style={styles.modalSub}>
              Select the costumes you want to return:
            </p>
            {returnMessage && (
              <div style={returnMessage.includes('✅') ?
                styles.successMsg : styles.errorMsg}>
                {returnMessage}
              </div>
            )}
            {borrowedCostumes.filter(c =>
              c.status === 'borrowed').map(c => (
                <div key={c.id} style={{
                  ...styles.returnItem,
                  background: selectedReturns.includes(c.id) ?
                    '#e8f0fe' : '#f8f9fa',
                  border: selectedReturns.includes(c.id) ?
                    '2px solid #0f3460' : '1px solid #eee',
                }}
                  onClick={() => toggleReturn(c.id)}>
                  <div style={styles.returnCheckbox}>
                    {selectedReturns.includes(c.id) ? '☑' : '☐'}
                  </div>
                  <div style={styles.returnInfo}>
                    <p style={styles.returnName}>
                      {c.category_name} {c.type_name}
                    </p>
                    <p style={styles.returnCode}>{c.costume_name}</p>
                  </div>
                </div>
              ))}
            {borrowedCostumes.filter(c =>
              c.status === 'borrowed').length === 0 && (
                <p style={styles.emptyText}>
                  All costumes already submitted for return!
                </p>
              )}
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => {
                  setShowReturnCostume(false);
                  setSelectedReturns([]);
                  setReturnMessage('');
                }}>
                Cancel
              </button>
              <button style={styles.doneBtn}
                onClick={handleReturnCostume}>
                Submit Return
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Instrument Modal */}
      {showReturnInstrument && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🎵 Return Instrument</h3>
            <p style={styles.modalSub}>
              Select the instruments you want to return:
            </p>
            {returnMessage && (
              <div style={returnMessage.includes('✅') ?
                styles.successMsg : styles.errorMsg}>
                {returnMessage}
              </div>
            )}
            {borrowedInstruments.filter(i =>
              i.status === 'borrowed').map(i => (
                <div key={i.id} style={{
                  ...styles.returnItem,
                  background: selectedReturns.includes(i.id) ?
                    '#e8f0fe' : '#f8f9fa',
                  border: selectedReturns.includes(i.id) ?
                    '2px solid #0f3460' : '1px solid #eee',
                }}
                  onClick={() => toggleReturn(i.id)}>
                  <div style={styles.returnCheckbox}>
                    {selectedReturns.includes(i.id) ? '☑' : '☐'}
                  </div>
                  <div style={styles.returnInfo}>
                    <p style={styles.returnName}>{i.category_name}</p>
                    <p style={styles.returnCode}>{i.instrument_name}</p>
                  </div>
                </div>
              ))}
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => {
                  setShowReturnInstrument(false);
                  setSelectedReturns([]);
                  setReturnMessage('');
                }}>
                Cancel
              </button>
              <button style={styles.doneBtn}
                onClick={handleReturnInstrument}>
                Submit Return
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              📅 Performances on {months[currentDate.getMonth()]} {selectedEvent.day}
            </h3>
            {selectedEvent.performances.map(p => (
              <div key={p.id} style={styles.eventCard}>
                <p style={styles.eventName}>{p.name}</p>
                <p style={styles.eventDetail}>📍 {p.location}</p>
                <p style={styles.eventDetail}>
                  ⏰ {p.call_time_announced ?
                    'To be announced' : p.call_time?.slice(0, 5)}
                </p>
                <span style={{
                  ...styles.eventStatus,
                  background: isEventDone(p.date) ? '#e0ffe0' : '#ffe0e0',
                  color: isEventDone(p.date) ? '#006600' : '#cc0000',
                }}>
                  {isEventDone(p.date) ? '✅ Done' : '🔴 Upcoming'}
                </span>
              </div>
            ))}
            <button style={styles.cancelBtn}
              onClick={() => setSelectedEvent(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  loading: { textAlign: 'center', padding: '50px', color: '#888' },
  banner: {
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    borderRadius: '16px', padding: '25px 30px',
    marginBottom: '20px', display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '10px',
  },
  bannerTitle: { color: 'white', margin: '0 0 5px 0', fontSize: '22px' },
  bannerSub: {
    color: 'rgba(255,255,255,0.6)', margin: 0,
    fontSize: '13px', textTransform: 'capitalize',
  },
  bannerDate: {
    color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '13px',
  },
  annBox: {
    background: '#fff9e6', borderRadius: '12px',
    padding: '15px 20px', marginBottom: '20px',
    borderLeft: '4px solid #f39c12',
  },
  annTitle: {
    margin: '0 0 10px 0', fontWeight: 'bold',
    color: '#cc7700', fontSize: '14px',
  },
  annItem: {
    marginBottom: '10px', paddingBottom: '10px',
    borderBottom: '1px solid #ffe9a0',
  },
  annItemTitle: {
    margin: '0 0 4px 0', fontWeight: 'bold',
    color: '#333', fontSize: '14px',
  },
  annItemContent: { margin: 0, color: '#555', fontSize: '13px' },
  cardsRow: {
    display: 'flex', gap: '15px',
    marginBottom: '20px', flexWrap: 'wrap',
  },
  card: {
    flex: '1', minWidth: '130px', background: 'white',
    borderRadius: '12px', padding: '15px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    cursor: 'pointer',
  },
  cardIcon: { fontSize: '24px', margin: '0 0 6px 0' },
  cardValue: {
    fontSize: '22px', fontWeight: 'bold',
    color: '#333', margin: '0 0 4px 0',
  },
  cardLabel: { fontSize: '11px', color: '#888', margin: '0 0 4px 0' },
  cardAction: { fontSize: '11px', color: '#0f3460', margin: 0 },
  mainRow: {
    display: 'flex', gap: '20px', flexWrap: 'wrap',
  },
  leftCol: { flex: '1', minWidth: '280px' },
  rightCol: { width: '300px' },
  box: {
    background: 'white', borderRadius: '16px',
    padding: '20px', marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  boxHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '15px',
  },
  boxTitle: {
    margin: '0 0 15px 0', fontSize: '15px',
    fontWeight: 'bold', color: '#333',
  },
  returnBtn: {
    padding: '6px 14px', background: '#0f3460',
    color: 'white', border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
  },
  weekRow: {
    display: 'flex', gap: '6px', justifyContent: 'space-between',
  },
  dayBox: {
    flex: 1, borderRadius: '10px',
    padding: '10px 4px', textAlign: 'center',
  },
  dayName: {
    margin: '0 0 4px 0', fontSize: '10px',
    fontWeight: 'bold', color: '#888',
  },
  dayDate: {
    margin: '0 0 4px 0', fontSize: '14px',
    fontWeight: 'bold', color: '#333',
  },
  attStatus: { margin: 0, fontSize: '14px' },
  emptyText: { color: '#aaa', fontSize: '13px', textAlign: 'center', padding: '10px 0' },
  borrowItem: {
    display: 'flex', alignItems: 'center',
    gap: '12px', padding: '10px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  borrowIcon: {
    fontSize: '24px', width: '40px',
    height: '40px', background: '#f8f9fa',
    borderRadius: '10px', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  borrowInfo: { flex: 1 },
  borrowName: {
    margin: '0 0 2px 0', fontWeight: 'bold',
    color: '#333', fontSize: '13px',
  },
  borrowCode: { margin: '0 0 2px 0', color: '#888', fontSize: '12px' },
  borrowDeadline: { margin: 0, fontSize: '11px' },
  borrowStatus: {
    padding: '3px 8px', borderRadius: '20px',
    fontSize: '10px', fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  fineItem: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '10px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  fineInfo: { flex: 1 },
  fineReason: { margin: '0 0 3px 0', color: '#333', fontSize: '13px' },
  fineDate: { margin: 0, color: '#aaa', fontSize: '11px' },
  fineRight: { textAlign: 'right' },
  fineAmount: {
    margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '15px',
  },
  finePill: {
    padding: '2px 8px', borderRadius: '20px',
    fontSize: '10px', fontWeight: 'bold',
  },
  calHeader: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: '15px',
  },
  calNavBtn: {
    background: '#f0f2f5', border: 'none',
    borderRadius: '8px', padding: '6px 10px',
    cursor: 'pointer', fontSize: '12px',
  },
  calTitle: {
    margin: 0, fontSize: '14px',
    fontWeight: 'bold', color: '#333',
  },
  dayLabels: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: '5px',
  },
  dayLabel: {
    textAlign: 'center', fontSize: '11px',
    fontWeight: 'bold', color: '#888', padding: '4px 0',
  },
  calGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '3px',
  },
  emptyCell: { height: '40px' },
  calDay: {
    height: '40px', display: 'flex',
    flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', borderRadius: '8px',
  },
  todayDay: { background: '#0f3460' },
  dayNum: { fontSize: '12px', color: '#333' },
  todayNum: { fontSize: '12px', color: 'white', fontWeight: 'bold' },
  dotsRow: { display: 'flex', gap: '2px', marginTop: '2px' },
  dot: { width: '5px', height: '5px', borderRadius: '50%' },
  legend: {
    display: 'flex', gap: '15px',
    justifyContent: 'center', marginTop: '12px',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '5px' },
  legendDot: {
    width: '8px', height: '8px', borderRadius: '50%',
  },
  legendText: { fontSize: '11px', color: '#888' },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 1001,
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '16px',
    padding: '30px', width: '100%', maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: {
    margin: '0 0 8px 0', fontSize: '18px',
    fontWeight: 'bold', color: '#333',
  },
  modalSub: { margin: '0 0 15px 0', color: '#888', fontSize: '13px' },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '10px', borderRadius: '8px',
    marginBottom: '12px', fontSize: '13px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '10px', borderRadius: '8px',
    marginBottom: '12px', fontSize: '13px',
  },
  returnItem: {
    display: 'flex', alignItems: 'center',
    gap: '12px', padding: '12px',
    borderRadius: '10px', marginBottom: '8px',
    cursor: 'pointer',
  },
  returnCheckbox: { fontSize: '20px', color: '#0f3460' },
  returnInfo: { flex: 1 },
  returnName: {
    margin: '0 0 3px 0', fontWeight: 'bold',
    color: '#333', fontSize: '14px',
  },
  returnCode: { margin: 0, color: '#888', fontSize: '12px' },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '20px' },
  cancelBtn: {
    flex: 1, padding: '12px', background: 'white',
    color: '#666', border: '1.5px solid #ddd',
    borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '14px',
  },
  doneBtn: {
    flex: 1, padding: '12px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
  },
  eventCard: {
    background: '#f8f9fa', borderRadius: '10px',
    padding: '15px', marginBottom: '10px',
  },
  eventName: {
    margin: '0 0 6px 0', fontWeight: 'bold',
    color: '#333', fontSize: '15px',
  },
  eventDetail: { margin: '0 0 4px 0', color: '#888', fontSize: '13px' },
  eventStatus: {
    display: 'inline-block', padding: '3px 10px',
    borderRadius: '20px', fontSize: '12px',
    fontWeight: 'bold', marginTop: '6px',
  },
};

export default MemberHome;